import { useCallback, useEffect, useMemo, useState } from "react";

import { useFiltersScope } from "~/components/filters/FiltersScope";
import { useDebouncedValue } from "~/hooks/useDebouncedValue";

import {
  MAX_CUSTOM_SPREAD_CENTS,
  spreadDecimalByFilterEnum,
} from "./constants";
import { SpreadFilterEnum } from "./types";

const toCents = (decimal: number) => Number((decimal * 100).toFixed(2));
const toDecimal = (cents: number) => Number((cents / 100).toFixed(4));

const getFilterValue = (minSpread?: number): SpreadFilterEnum => {
  if (minSpread === undefined) return SpreadFilterEnum.All;

  const preset = Object.entries(spreadDecimalByFilterEnum).find(
    ([, value]) => value === minSpread
  );

  return preset?.[0]
    ? (preset[0] as SpreadFilterEnum)
    : SpreadFilterEnum.Custom;
};

export const useSpreadFilter = () => {
  const { filters, updateFilters, registerOnClearAllFilters } = useFiltersScope();
  const filterValue = useMemo(
    () => getFilterValue(filters.minSpread),
    [filters.minSpread]
  );
  const filterCustomCents =
    filterValue === SpreadFilterEnum.Custom && filters.minSpread !== undefined
      ? toCents(filters.minSpread)
      : null;

  const [isCustomDraft, setIsCustomDraft] = useState(false);
  const [customSpreadCents, setCustomSpreadCents] = useState<number | null>(
    null
  );
  const debouncedCustomSpreadCents = useDebouncedValue(customSpreadCents, 350);

  const selectedValue = isCustomDraft ? SpreadFilterEnum.Custom : filterValue;
  const displayedCustomCents = isCustomDraft
    ? customSpreadCents
    : filterCustomCents;

  useEffect(() => {
    return registerOnClearAllFilters(() => {
      setIsCustomDraft(false);
      setCustomSpreadCents(null);
    });
  }, [registerOnClearAllFilters]);

  useEffect(() => {
    if (!isCustomDraft || !debouncedCustomSpreadCents) return;

    updateFilters({
      minSpread: toDecimal(
        Math.min(debouncedCustomSpreadCents, MAX_CUSTOM_SPREAD_CENTS)
      ),
    });
  }, [debouncedCustomSpreadCents, isCustomDraft, updateFilters]);

  const selectSpread = useCallback(
    (value: SpreadFilterEnum | null) => {
      if (!value) return;

      if (value === SpreadFilterEnum.Custom) {
        setIsCustomDraft(true);
        setCustomSpreadCents(filterCustomCents);
        return;
      }

      setIsCustomDraft(false);
      setCustomSpreadCents(null);
      updateFilters({ minSpread: spreadDecimalByFilterEnum[value] });
    },
    [filterCustomCents, updateFilters]
  );

  const resetSpread = useCallback(() => {
    setIsCustomDraft(false);
    setCustomSpreadCents(null);
    updateFilters({ minSpread: undefined });
  }, [updateFilters]);

  const changeCustomSpreadCents = useCallback((value: number | null) => {
    setCustomSpreadCents(
      value === null ? null : Math.min(value, MAX_CUSTOM_SPREAD_CENTS)
    );
  }, []);

  return {
    selectedValue,
    customSpreadCents: displayedCustomCents,
    selectSpread,
    resetSpread,
    changeCustomSpreadCents,
  };
};
